/**
 * Data layer for `scaling-payouts-volume-breakpoints`. Thin typed fetch wrappers over /api/v1/scaling-payouts-volume-breakpoints.
 * Raw `fetch()` is confined to this file per the dash contract.
 */

export interface ScalingPayoutsVolumeBreakpointsRow {
	id: string;
	tenant_id: string;
	created_at: number;
	updated_at: number;
	[key: string]: unknown;
}

export interface ListScalingPayoutsVolumeBreakpointsResponse {
	count: number;
	items: ScalingPayoutsVolumeBreakpointsRow[];
}

export interface CreateScalingPayoutsVolumeBreakpointsBody {
	[key: string]: unknown;
}

const BASE = "/api/v1/scaling-payouts-volume-breakpoints";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function list(): Promise<ListScalingPayoutsVolumeBreakpointsResponse> {
	const res = await fetch(BASE, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListScalingPayoutsVolumeBreakpointsResponse>(res);
}

export async function create(body: CreateScalingPayoutsVolumeBreakpointsBody): Promise<ScalingPayoutsVolumeBreakpointsRow> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body ?? {}),
	});
	return parseOrThrow<ScalingPayoutsVolumeBreakpointsRow>(res);
}

export async function remove(id: string): Promise<{ removed: boolean }> {
	const res = await fetch(`${BASE}/${encodeURIComponent(id)}`, {
		method: "DELETE",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<{ removed: boolean }>(res);
}

export const scalingPayoutsVolumeBreakpointsQueryKeys = {
	all: ["scaling-payouts-volume-breakpoints"] as const,
	list: () => [...scalingPayoutsVolumeBreakpointsQueryKeys.all] as const,
	detail: (id: string) => [...scalingPayoutsVolumeBreakpointsQueryKeys.all, id] as const,
};
